import React, { useCallback, useEffect, useState, useMemo } from "react";
import usePatient from "../../../../hooks/usePatient";
import Loader from "../../../../components/Loader";
import { DataNotFound } from "../../../../components/DataNotFound/DataNotFound";
import Swal from "sweetalert2";
import { error as errorAlert } from "../../../../components/SwalAlertData";
import { Card } from "react-bootstrap";
import hceService from "../../../../services/hceServices/hceService";
import { typeHCE } from "../../../../constants/api.constants";

// Lista de campos a filtrar (no mostrar al usuario)
const fieldsToFilter = [
  'id',
  'patient_id',
  'statusId', 
  'categoryId',
  'criticalityId'
];

const HCEGeneric = ({ type }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [errorState, setErrorState] = useState(null);
  
  const patient = usePatient();

  // Función para procesar un objeto y convertirlo en array de strings
  const processObjectData = useCallback((info) => {
    const processedData = [];
    
    const addKeyValue = (key, value) => {
      // Filtrar campos técnicos que no queremos mostrar
      if (fieldsToFilter.includes(key)) {
        return;
      }
      
      if (value !== null && value !== undefined && value !== "") {
        processedData.push(`${key}: ${value}`);
      }
    };

    Object.entries(info).forEach(([key, value]) => {
      if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        addKeyValue(key, value);
      } else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        // Procesar objetos anidados
        Object.entries(value).forEach(([nestedKey, nestedValue]) => {
          addKeyValue(`${key}.${nestedKey}`, nestedValue);
        });
      } else if (Array.isArray(value)) {
        // Manejar arrays
        value.forEach((item, index) => {
          if (typeof item === "object" && item !== null) {
            Object.entries(item).forEach(([itemKey, itemValue]) => {
              addKeyValue(`${key}[${index}].${itemKey}`, itemValue);
            });
          } else {
            addKeyValue(`${key}[${index}]`, item);
          }
        });
      } else if (value !== null) {
        addKeyValue(key, String(value));
      }
    });

    return processedData;
  }, []);

  const fetchData = useCallback(async () => {
    if (!patient.patient.id_usual_institution || !patient.idPatient || !type) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorState(null);

    try {
      const response = await hceService(
        patient.patient.id_usual_institution,
        type,
        patient.idPatient,
        patient.patient.id
      );

      if (Array.isArray(response) && response.length > 0) {
        const processedData = response.map((item, index) => ({
          id: item.id || `item-${index}`, // Mejor key para React
          data: processObjectData(item)
        }));
        setData(processedData);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error("Error fetching HCE data:", err);
      setErrorState("Hubo un error al solicitar datos");
      Swal.fire(errorAlert("Hubo un error al solicitar datos"));
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [patient.patient.id_usual_institution, patient.idPatient, patient.patient.id, type, processObjectData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Memoizar el componente de datos para evitar re-renders innecesarios
  const dataCards = useMemo(() => {
    if (data.length === 0) return null;

    return data.map((item, index) => (
      <Card key={item.id || index} className="mb-3 shadow-sm">
        <Card.Header>
          <strong>Registro {index + 1}</strong>
        </Card.Header>
        <Card.Body>
          <blockquote className="blockquote mb-0">
            {item.data.length > 0 ? (
              item.data.map((itemData, dataIndex) => (
                <p key={`${item.id}-${dataIndex}`} className="mb-1">
                  {itemData}
                </p>
              ))
            ) : (
              <p className="text-muted">Sin datos disponibles</p>
            )}
          </blockquote>
        </Card.Body>
      </Card>
    ));
  }, [data]);

  // Estados de carga y error
  if (loading) {
    return <Loader isActive={loading} />;
  }

  if (errorState) {
    return <DataNotFound text={`Error: ${errorState}`} />;
  }

  return (
    <div className="in">
      {data.length > 0 ? (
        dataCards
      ) : (
        <DataNotFound text={typeHCE[type]} />
      )}
    </div>
  );
};

export default HCEGeneric;
